import Link from 'next/link';
import Image from 'next/image';
import PropTypes from 'prop-types';
import { ArrowRight } from 'lucide-react';

/**
 * Renders up to 3 related posts. Uses manualOverride (ACF) when present, otherwise relatedPosts from server.
 * @param {Object} props
 * @param {Array} [props.manualOverride] - ACF relatedPostsOverride (array of Post nodes)
 * @param {Array} [props.relatedPosts] - Fetched by category when no override
 * @param {string} [props.currentSlug] - Current post slug (filter out if present in list)
 * @param {string} [props.basePath] - Base path for links (/blog or /blogi)
 * @param {string} [props.lang] - 'en' | 'fi' for copy
 */
export function RelatedPosts({ manualOverride, relatedPosts, currentSlug, basePath = '/blog', lang = 'en' }) {
  const raw = manualOverride?.length ? manualOverride : relatedPosts;
  const list = (raw ?? [])
    .filter((p) => p?.slug && p.slug !== currentSlug)
    .slice(0, 3);
  if (!list.length) return null;

  const heading = lang === 'fi' ? 'Aiheeseen liittyviä artikkeleita' : 'Related articles';
  const readMore = lang === 'fi' ? 'Lue lisää' : 'Read more';

  return (
    <section className="mt-16 pt-12 border-t border-gray-200">
      <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
        {heading}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {list.map((post) => {
          const img = post?.featuredImage?.node?.sourceUrl;
          const category = post?.categories?.nodes?.[0]?.name;
          return (
            <Link
              key={post.databaseId ?? post.slug ?? post.title}
              href={`${basePath.replace(/\/$/, '')}/${post.slug}`}
              className="group block border border-gray-200 rounded-xl overflow-hidden bg-white hover:border-brand hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 touch-manipulation"
            >
              {img && (
                <div className="aspect-[16/10] bg-gray-100 overflow-hidden relative">
                  <Image
                    src={img}
                    alt=""
                    fill
                    sizes="(max-width: 768px) 100vw, 33vw"
                    className="object-cover group-hover:scale-105 transition-transform duration-300"
                    unoptimized
                  />
                </div>
              )}
              <div className="p-5">
                {category && (
                  <span className="text-xs font-semibold uppercase tracking-wide text-brand mb-2 block">
                    {category}
                  </span>
                )}
                <h4 className="font-semibold text-lg text-gray-900 mb-2 line-clamp-2 group-hover:text-brand transition-colors">
                  {post.title}
                </h4>
                {post.excerpt && (
                  <>
                    {/* WordPress excerpt – trusted source (docs/SECURITY.md) */}
                    <p
                      className="text-gray-600 text-sm line-clamp-3"
                      dangerouslySetInnerHTML={{ __html: post.excerpt }}
                    />
                  </>
                )}
                <span className="inline-flex items-center gap-1 text-sm font-medium text-brand mt-2 group-hover:gap-2 transition-all">
                  {readMore}
                  <ArrowRight className="w-4 h-4 shrink-0" />
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

RelatedPosts.propTypes = {
  manualOverride: PropTypes.arrayOf(PropTypes.object),
  relatedPosts: PropTypes.arrayOf(PropTypes.object),
  currentSlug: PropTypes.string,
  basePath: PropTypes.string,
  lang: PropTypes.oneOf(['en', 'fi']),
};
